import React, { useState } from "react";
import { motion } from "framer-motion";
import { FiMapPin, FiPhone, FiMail, FiSend } from "react-icons/fi";
import PhoneInput from "react-phone-input-2";
import { ToastContainer, toast } from "react-toastify";
import emailjs from "emailjs-com"; // 🆕 Import EmailJS
import "react-phone-input-2/lib/style.css";
import "react-toastify/dist/ReactToastify.css";
import "../Styles/Contact.css";
import IntlMessages from "../../../intl/IntlMessages";
import { useIntl } from "react-intl";

const Contact = () => {
  const intl = useIntl();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    title: "",
    option: "", // 🆕 Nouveau champ pour le menu déroulant
    message: "",
    countryCode: "cm",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const contactInfo = [
    {
      icon: <FiMapPin />,
      title: <IntlMessages id="team.address" />,
      details: [<IntlMessages id="team.location" />],
    },
    {
      icon: <FiPhone />,
      title: <IntlMessages id="team.phone" />,
      details: ["+237 698 476 609", "+237 620 196 116"],
    },
    {
      icon: <FiMail />,
      title: <IntlMessages id="team.email" />,
      details: ["estatiasci@gmail.com"],
    },
  ];

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const validateInput = (name, value) => {
    switch (name) {
      case "name":
        return value.trim().length >= 2;
      case "email":
        return validateEmail(value);
      case "title":
        return value.trim().length >= 3;
      case "message":
        return value.trim().length >= 10;
      case "option": // 🆕 Menu déroulant obligatoire
        return value.trim() !== "";
      default:
        return true;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...formData, [name]: value };
    setFormData(updatedFormData);

    const isValid = validateInput(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: isValid ? "" : `Le champ ${name} est invalide`,
    }));

    const formIsValid = ["name", "email", "title", "message", "option"].every(
      (field) => validateInput(field, updatedFormData[field])
    );
    setIsFormValid(formIsValid);
  };

  const handlePhoneChange = (value, data) => {
    setFormData((prev) => ({
      ...prev,
      phone: value,
      countryCode: data.countryCode,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredFieldsValid = [
      "name",
      "email",
      "title",
      "message",
      "option",
    ].every((field) => validateInput(field, formData[field]));

    if (!requiredFieldsValid) {
      toast.error("Veuillez remplir correctement tous les champs requis");
      return;
    }

    setIsLoading(true);

    try {
      await emailjs.send(
        "service_fjpesk4", // 🔑 Remplace par ton service ID
        "template_f59zx5f", // 🔑 Remplace par ton template ID
        formData,
        "d2Mhnh5sMQB1Fbfmw" // 🔑 Remplace par ta clé publique
      );

      toast.success("Message envoyé avec succès !");
      setFormData({
        name: "",
        email: "",
        phone: "",
        title: "",
        option: "",
        message: "",
        countryCode: "cm",
      });

      setErrors({});
      setIsFormValid(false);
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message. Veuillez réessayer.");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="contact-section">
      <ToastContainer position="top-right" />
      <motion.div
        className="contact-container"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="contact-header">
          <h2>
            <IntlMessages id="content.getInTouch" />
          </h2>
          <p>
            <IntlMessages id="team.ourPresentTeam" />
          </p>
        </div>

        <div className="contact-content">
          <motion.div
            className="contact-info-cards"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {contactInfo.map((info, index) => (
              <div key={index} className="info-card">
                <div className="info-icon">{info.icon}</div>
                <h3>{info.title}</h3>
                {info.details.map((detail, idx) => (
                  <p key={idx}>{detail}</p>
                ))}
              </div>
            ))}
          </motion.div>

          <motion.div
            className="contact-form-wrapper"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="contact-form">
              {/* 🆕 Menu déroulant */}
              <div className="input-group floating">
                <select
                  aria-invalid={!!errors.option}
                  ar
                  id="option"
                  name="option"
                  value={formData.option}
                  onChange={handleChange}
                >
                  <option value="">{`${intl.formatMessage({
                    id: "home.emailJs",
                  })}`}</option>
                  <option value="Real Estate">
                    {`${intl.formatMessage({ id: "sections.realEstate" })}`}
                  </option>
                  <option value="Event">
                    {`${intl.formatMessage({ id: "eventManagement.event" })}`}
                  </option>
                  <option value="IT Service">
                    {`${intl.formatMessage({ id: "it.solutions" })}`}
                  </option>
                  <option value="Social Platform">
                    {`${intl.formatMessage({
                      id: "expertise.socialPlatform",
                    })}`}
                  </option>
                  <option value="Other">
                    {`${intl.formatMessage({ id: "content.others" })}`}
                  </option>
                </select>
                <label htmlFor="option">{`${intl.formatMessage({
                  id: "home.label",
                })}`}</label>
                {errors.option && <span>{errors.option}</span>}
              </div>

              {/* Nom + Email */}
              <div className="form-grid">
                <div className="input-group floating">
                  <input
                    type="text"
                    id="name"
                    required
                    name="name"
                    aria-invalid={!!errors.name}
                    value={formData.name}
                    onChange={handleChange}
                    className={`floating-input ${
                      formData.name ? "has-value" : ""
                    } ${errors.name ? "error" : ""}`}
                    placeholder=""
                  />
                  <label htmlFor="name" className="floating-label">
                    <IntlMessages id="contact.formName" />
                  </label>
                  {errors.name && (
                    <span className="error-message">{errors.name}</span>
                  )}
                </div>

                <div className="input-group floating">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    aria-invalid={!!errors.email}
                    value={formData.email}
                    onChange={handleChange}
                    className={`floating-input ${
                      formData.email ? "has-value" : ""
                    } ${errors.email ? "error" : ""}`}
                    placeholder=" "
                  />
                  <label htmlFor="email" className="floating-label">
                    <IntlMessages id="contact.formEmail" />
                  </label>
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>
              </div>

              {/* Téléphone */}
              <div className="input-group">
                <PhoneInput
                  country={formData.countryCode}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  preferredCountries={["cm"]}
                  enableSearch={true}
                />
              </div>

              {/* Sujet */}
              {/* Message */}
              <div className="input-group floating">
                <input
                  type="text"
                  id="title"
                  name="title"
                  aria-invalid={!!errors.title}
                  value={formData.title}
                  onChange={handleChange}
                  className={`floating-input ${
                    formData.title ? "has-value" : ""
                  } ${errors.title ? "error" : ""}`}
                  placeholder=" "
                />
                <label htmlFor="title" className="floating-label">
                  <IntlMessages id="contact.formSubject" />
                </label>
                {errors.title && (
                  <span className="error-message">{errors.title}</span>
                )}
              </div>

              <div className="input-group floating">
                <textarea
                  id="message"
                  name="message"
                  aria-invalid={!!errors.message}
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className={`floating-input ${
                    formData.message ? "has-value" : ""
                  } ${errors.message ? "error" : ""}`}
                  placeholder=" "
                />
                <label htmlFor="message" className="floating-label">
                  <IntlMessages id="contact.formMessage" />
                </label>
                {errors.message && (
                  <span className="error-message">{errors.message}</span>
                )}
              </div>

              {/* Bouton */}
              <div className="submit-button-wrapper">
                <button
                  type="submit"
                  className="submit-button"
                  disabled={!isFormValid || isLoading}
                >
                  {isLoading ? (
                    <div className="loading-spinner" />
                  ) : (
                    <>
                      <IntlMessages id="contact.send" />
                      <FiSend />
                    </>
                  )}
                </button>
                {!isFormValid && (
                  <span className="form-validation-message">
                    {["name", "email", "title", "message"].some(
                      (key) => !formData[key]
                    ) ? (
                      <IntlMessages id="contact.missingFields" />
                    ) : (
                      <IntlMessages id="contact.correct" />
                    )}
                  </span>
                )}
                <h6>
                  <IntlMessages id="contact.whatsapp" />
                </h6>
              </div>
            </form>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Contact;
